import { api } from "@repo/convex/api";
import { PROJECT_LEVEL_ROUTE_PATHS } from "@repo/shared";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EdgeResizeHandle } from "@/components/sidebar-resize-handle";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useOrganization } from "@/contexts/organization-context";
import { useTabsActions, useTabsInternalActions } from "@/contexts/tabs-context";
import { shouldClearTransientProjectSearchParams } from "@/features/project/lib/project-tab-search-params";
import { createProjectNavigationPolicy } from "@/features/project/routing/project-navigation-policy";
import { ProjectRouteProvider } from "@/features/project/routing/project-route-context";
import {
  DocumentEvidenceInteractionProvider,
  ProjectMentionInteractionProvider,
  ReferencePresentationProvider,
} from "@/features/rich-text";
import {
  clampRightPanelWidth,
  emitRightPanelWidthChange,
  resolveRightPanelWidth,
  saveRightPanelWidth,
} from "@/lib/right-panel-width";
import { resolveProjectWorkspaceNavigationTarget, type TabMemory } from "@/routing/core/locations";
// Contexts
import { RightSidebarProvider, useRightSidebarUi } from "../contexts";
// Extracted hooks
import { useLayoutSidebars, useProjectBySlug, useProjectFrameworkStructure } from "../hooks";
import { createProjectReferencePresentationResolver } from "../lib/reference-presentation";
// Types and constants
import {
  RIGHT_SIDEBAR_DEFAULT,
  RIGHT_SIDEBAR_MAX,
  RIGHT_SIDEBAR_MIN,
  type TabSearchParams,
} from "../types";
// Extracted components
import { RightSidebar } from "./panels";
import { ProjectContent } from "./project-content";
import { ProjectNotFound } from "./project-not-found";

// ============================================================================
// Props Interface
// ============================================================================

interface ProjectLayoutProps {
  tabId: string;
  projectId: string;
  currentPath: string;
  searchParams: TabSearchParams;
  memory: TabMemory;
}

// ============================================================================
// Main ProjectLayout Component
// ============================================================================

export function ProjectLayout({
  tabId,
  projectId,
  currentPath,
  searchParams,
  memory,
}: ProjectLayoutProps) {
  const { getTab, navigateTab, updateTabSearchParams } = useTabsActions();
  const { registerNavigationPolicy, renameTab, unregisterNavigationPolicy, updateTabMemory } =
    useTabsInternalActions();
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const hasReplayedInitialAssistantPolicyRef = useRef(false);
  const previousFrameworkPendingRef = useRef<boolean | null>(null);

  // Fetch project from Convex (projectId is the slug)
  const convexProject = useProjectBySlug(organizationId, projectId);

  useEffect(() => {
    if (convexProject && getTab(tabId)?.name !== convexProject.name) {
      renameTab(tabId, convexProject.name);
    }
  }, [convexProject, getTab, renameTab, tabId]);

  // Extract Convex project ID for data fetching
  const convexProjectId = convexProject?._id ?? null;
  const frameworkStructure = useProjectFrameworkStructure(convexProjectId);
  const projectNavigationPolicy = useMemo(
    () => createProjectNavigationPolicy(frameworkStructure),
    [frameworkStructure]
  );
  const referencePresentationResolver = useMemo(
    () =>
      createProjectReferencePresentationResolver({
        frameworkStructure,
        projectResourceId: projectId,
      }),
    [frameworkStructure, projectId]
  );

  useEffect(() => {
    registerNavigationPolicy(tabId, projectNavigationPolicy);
    return () => unregisterNavigationPolicy(tabId);
  }, [projectNavigationPolicy, registerNavigationPolicy, tabId, unregisterNavigationPolicy]);

  useEffect(() => {
    const frameworkPending = frameworkStructure === undefined;
    const frameworkLoadedAfterPending =
      previousFrameworkPendingRef.current === true && !frameworkPending;
    previousFrameworkPendingRef.current = frameworkPending;

    const shouldReplayAssistantPolicy =
      !hasReplayedInitialAssistantPolicyRef.current &&
      currentPath.startsWith(PROJECT_LEVEL_ROUTE_PATHS.aiAssistant);

    if (!frameworkLoadedAfterPending && !shouldReplayAssistantPolicy) {
      return;
    }

    hasReplayedInitialAssistantPolicyRef.current = true;
    // Replaying the policy re-canonicalizes the path and rebuilds tab memory for the
    // path the tab is ALREADY on — it is not a fresh navigation. Passing the tab's own
    // search params as the requested set keeps the replay idempotent: without them the
    // policy's transient-reset rule (project-level clarification/snapshot/guard
    // paths) wiped a cold deep link's `crs`/`crv` the moment the framework structure
    // resolved, so notification navigation into /clarifications lost its thread.
    navigateTab(tabId, currentPath, searchParams, { replace: true, skipGuard: true });
  }, [currentPath, frameworkStructure, navigateTab, searchParams, tabId]);

  // Navigation within project
  const navigateWithinProject = useCallback(
    (path: string, nextSearchParams?: TabSearchParams, options?: { skipGuard?: boolean }) => {
      const target = resolveProjectWorkspaceNavigationTarget({
        projectResourceId: projectId,
        path,
      });

      if (target.kind === "app") {
        void navigate({ href: target.href });
        return;
      }

      navigateTab(tabId, target.currentPath, nextSearchParams, options);
    },
    [navigate, navigateTab, projectId, tabId]
  );

  // ============================================================================
  // CONDITIONAL RETURNS (after all hooks)
  // ============================================================================

  // Loading state for Convex project
  if (convexProject === undefined) {
    return (
      <main className="flex-1 flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p className="text-lg">Loading project...</p>
        </div>
      </main>
    );
  }

  if (!convexProject) {
    return <ProjectNotFound slug={projectId} />;
  }

  return (
    <ProjectRouteProvider
      currentPath={currentPath}
      frameworkStructure={frameworkStructure}
      searchParams={searchParams}
      navigate={navigateWithinProject}
    >
      <RightSidebarProvider
        projectId={convexProjectId!}
        searchParams={searchParams}
        memory={memory}
        tabId={tabId}
        updateTabSearchParams={updateTabSearchParams}
        updateTabMemory={updateTabMemory}
      >
        <ProjectMentionInteractionProvider
          projectId={convexProjectId!}
          onNavigate={navigateWithinProject}
        >
          <DocumentEvidenceInteractionProvider projectId={convexProjectId!}>
            <ReferencePresentationProvider resolver={referencePresentationResolver}>
              <ProjectLayoutContent convexProjectId={convexProjectId!} currentPath={currentPath} />
            </ReferencePresentationProvider>
          </DocumentEvidenceInteractionProvider>
        </ProjectMentionInteractionProvider>
      </RightSidebarProvider>
    </ProjectRouteProvider>
  );
}

// ============================================================================
// Inner Layout (inside provider — can use context)
// ============================================================================

export function ProjectLayoutContent({
  convexProjectId,
  currentPath,
}: {
  convexProjectId: string;
  currentPath: string;
}) {
  // Read right sidebar UI state directly to compute the visibility flag
  const { open, isMobile, openMobile, setOpenMobile } = useRightSidebarUi();
  const preferences = useQuery(api.userPreferences.get, {});
  const savedRightPanelDefault = preferences?.ui?.defaultRightPanelWidth ?? RIGHT_SIDEBAR_DEFAULT;
  const [desktopRightWidth, setDesktopRightWidth] = useState(() =>
    resolveRightPanelWidth(savedRightPanelDefault)
  );
  const [isRightPanelResizing, setIsRightPanelResizing] = useState(false);

  const shouldHideRightPanel = shouldClearTransientProjectSearchParams(currentPath);

  // Composition hook: activates the auto-collapse state machine
  useLayoutSidebars({ shouldHideRightPanel });

  // Mobile: auto-close Sheet on hidden routes
  useEffect(() => {
    if (isMobile && shouldHideRightPanel && openMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, shouldHideRightPanel, openMobile, setOpenMobile]);

  // Mobile: mirror right-sidebar URL intent into the Sheet (LAT-575). A deep link such as a
  // clarification notification sets rc=false (→ `open`), which opens the desktop shelf but
  // not the mobile Sheet — `openMobile` is independent of the URL. Open the Sheet once per
  // intent so notifications reach the thread on mobile, matching desktop. The latch resets
  // only when the intent clears, so a manual dismissal is never overridden.
  const rightPanelUrlIntentOpen = open && !shouldHideRightPanel;
  const reconciledMobileOpenRef = useRef(false);
  useEffect(() => {
    if (!isMobile || !rightPanelUrlIntentOpen) {
      reconciledMobileOpenRef.current = false;
      return;
    }
    if (!reconciledMobileOpenRef.current && !openMobile) {
      reconciledMobileOpenRef.current = true;
      setOpenMobile(true);
    }
  }, [isMobile, rightPanelUrlIntentOpen, openMobile, setOpenMobile]);

  useEffect(() => {
    if (isMobile) return;
    setDesktopRightWidth(resolveRightPanelWidth(savedRightPanelDefault));
  }, [isMobile, savedRightPanelDefault]);

  return (
    <>
      <div className="flex flex-1 min-w-0 overflow-hidden">
        {/* Center Content — fills remaining space */}
        <main className="h-full flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 overflow-x-hidden overflow-y-auto flex flex-col">
            <ProjectContent projectId={convexProjectId} />
          </div>
        </main>

        {/* Right Sidebar — desktop animated shelf */}
        <AnimatePresence initial={false}>
          {!isMobile && open && (
            <motion.div
              key="right-sidebar"
              initial={{ width: 0 }}
              animate={{
                width: shouldHideRightPanel ? 0 : desktopRightWidth,
              }}
              exit={{ width: 0 }}
              transition={
                isRightPanelResizing
                  ? { duration: 0 }
                  : { duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }
              }
              className="relative shrink-0 overflow-hidden"
              data-right-sidebar-shelf
              data-right-sidebar-width={desktopRightWidth}
            >
              <div
                className="relative h-full"
                style={{ width: desktopRightWidth }}
                {...(shouldHideRightPanel ? { inert: true, "aria-hidden": true as const } : {})}
              >
                <EdgeResizeHandle
                  side="right"
                  currentWidth={desktopRightWidth}
                  minWidth={RIGHT_SIDEBAR_MIN}
                  maxWidth={RIGHT_SIDEBAR_MAX}
                  ariaLabel="Resize project tools panel"
                  resolveResizeContainer={(element) =>
                    element?.closest<HTMLElement>("[data-right-sidebar-shelf]") ?? null
                  }
                  onResizeStart={() => setIsRightPanelResizing(true)}
                  onResizeEnd={() => setIsRightPanelResizing(false)}
                  onWidthChange={(width) => {
                    setDesktopRightWidth(clampRightPanelWidth(width));
                  }}
                  onWidthCommit={(width) => {
                    const nextWidth = clampRightPanelWidth(width);
                    setDesktopRightWidth(nextWidth);
                    saveRightPanelWidth(nextWidth);
                    emitRightPanelWidthChange(nextWidth);
                  }}
                  testId="right-sidebar-resize-handle"
                />
                <RightSidebar />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Right Sidebar — mobile Sheet drawer (opened via EntityHeader triggers) */}
      {isMobile && (
        <Sheet open={openMobile} onOpenChange={setOpenMobile}>
          <SheetContent side="right" className="w-[85vw] max-w-[400px] p-0" showCloseButton={false}>
            <SheetHeader className="sr-only">
              <SheetTitle>Project Tools</SheetTitle>
              <SheetDescription>
                Versions, Guardian, Clarification Requests, and Claudia
              </SheetDescription>
            </SheetHeader>
            <RightSidebar />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
